import { useEffect, useMemo, useRef } from "react";
import {
	Easing,
	runOnJS,
	useSharedValue,
	withDelay,
	withSequence,
	withTiming,
} from "react-native-reanimated";
import useStabilizedCallback from "src/hooks/useStabilizedCallback";
import { getCurTime_MS } from "src/shared/helpers/timeHelpers";
import { LoadingProps } from "../types";

const getNumSegments = (diff: number) => {
	if (diff < 0.1) {
		return 1;
	}
	return 2 + Math.floor(Math.random() * 2);
};
const randBetween = (min: number, max: number) => Math.random() * (max - min) + min;

const useLoading = ({
	initialProgress = 0,
	estimatedTime = 5000,
	onAnimationComplete,
	isActive = true,
	...props
}: LoadingProps) => {
	// Variable setup
	/**
	 * Between 0 and 1. Loading bar progress.
	 */
	const progressSV = useSharedValue(initialProgress);
	const numTotal = useMemo(
		() => (props.numTotal != null ? Math.max(props.numTotal ?? 1, 1) : 1),
		[props.numTotal],
	);
	const numComplete = useMemo(
		() =>
			Math.min(
				props.numComplete != null
					? Math.max(props.numComplete ?? 0, 0)
					: props.complete
						? numTotal
						: 0,
				numTotal,
			),
		[numTotal, props.complete, props.numComplete],
	);
	const estimatedTimeRef = useRef(estimatedTime);
	const numTotalRef = useRef(numTotal);
	const numCompleteRef = useRef(numComplete);
	const onAnimationCompleteRef = useRef(onAnimationComplete);
	useEffect(() => {
		estimatedTimeRef.current = estimatedTime;
		numTotalRef.current = numTotal;
		numCompleteRef.current = numComplete;
		onAnimationCompleteRef.current = onAnimationComplete;
	}, [estimatedTime, numTotal, numComplete, onAnimationComplete]);

	const startRef = useRef<number | null>(null);
	useEffect(() => {
		if (isActive) {
			startRef.current = getCurTime_MS();
		}
	}, [isActive]);

	const setArbitraryProgress = useStabilizedCallback(() => {
		if (progressSV.value >= 1) return;
		if (startRef.current == null) return;
		const curTime = getCurTime_MS();
		const timeElapsed = curTime - startRef.current;
		const timeRemaining = Math.max(estimatedTimeRef.current - timeElapsed, 1000);
		const timeRemainingForSegment =
			timeRemaining / (numTotalRef.current - numCompleteRef.current);

		const maxNewProgress = Math.min(0.9, (numCompleteRef.current + 1) / numTotalRef.current);
		const totalDiff = maxNewProgress - progressSV.value;
		const targetProgress = Math.min(
			// Don't want it to always stall on a specific number
			progressSV.value + randBetween(0.75, 0.85) * totalDiff,
			maxNewProgress,
		);
		const finalTargetProgress = Math.min(
			progressSV.value + randBetween(0.9, 1) * totalDiff,
			maxNewProgress,
		);
		if (totalDiff < 0.2) {
			progressSV.value = withSequence(
				withTiming(targetProgress, {
					duration: 1.2 * timeRemainingForSegment,
					easing: Easing.inOut(Easing.poly(2)),
				}),
				withTiming(finalTargetProgress, {
					duration: 3 * estimatedTimeRef.current,
					easing: Easing.bezier(0.31, 0.25, 0.2, 0.93),
				}),
			);
		} else {
			progressSV.value = withSequence(
				withTiming(0.8 * targetProgress, {
					duration: timeRemainingForSegment * 0.6,
					easing: Easing.inOut(Easing.poly(2)),
				}),
				withDelay(
					500,
					withSequence(
						withTiming(targetProgress, {
							duration: timeRemainingForSegment * 0.4,
							easing: Easing.inOut(Easing.poly(3)),
						}),
						withTiming(finalTargetProgress, {
							duration: 3 * estimatedTimeRef.current,
							easing: Easing.bezier(0.31, 0.25, 0.2, 0.93),
						}),
					),
				),
			);
		}
	}, [progressSV]);

	// Forcing progress bar animation when new information
	useEffect(() => {
		if (!isActive) return;
		if (numComplete === numTotal) {
			progressSV.value = withTiming(
				1,
				{
					duration: 300,
				},
				() => {
					if (onAnimationCompleteRef.current) {
						runOnJS(onAnimationCompleteRef.current)();
					}
				},
			);
		} else {
			progressSV.value = withTiming(
				numComplete / numTotal,
				{
					duration: 600,
				},
				() => {
					runOnJS(setArbitraryProgress)();
				},
			);
		}
	}, [numTotal, numComplete, progressSV, isActive, setArbitraryProgress]);

	return {
		progressSV,
	};
};

export default useLoading;
